// The demo's verification runner.
//
// The demo verifies RECORDED receipt packages produced by an in-process
// demo witness. It therefore uses the "self" trust-anchor mode: the
// witness signatures are checked against the witness key bundled inside
// the receipt (an integrity self-check), NOT against an independently
// published witness key. The verification LOGIC is the real
// verifyReceiptPackage from @sequesign/lib — the same code an auditor
// runs — reached through the in-memory virtual filesystem the browser
// shims provide.

import { verifyReceiptPackage, witnessKeysFromReceipt } from "@sequesign/lib/verify";
import type { AgentActionReceipt, VerificationReport, WitnessKey } from "@sequesign/lib/types";
import path from "path-browserify";

import { installReceiptInVirtualFs } from "../load/install.js";
import { getFile, registerFile } from "../shims/virtual-fs.js";
import type { LoadedReceipt } from "../load/types.js";
import type { SchemaOverride } from "../break.js";

export interface DemoVerificationResult {
  report: VerificationReport;
  receipt: AgentActionReceipt;
}

export interface RunDemoOptions {
  // When present (the "tamper-schema" break), a substitute schema is
  // registered into the virtual FS at its resolved path BEFORE verification.
  // Because the fs shim's readFile checks the virtual FS before falling
  // through to the registry fetch, the verifier loads THIS schema, recomputes
  // a hash that no longer matches the one the receipt committed, and reports a
  // schema-hash mismatch. The receipt package is never modified.
  schemaOverride?: SchemaOverride;
  // EXTERNAL trust-anchor mode (Live mode). When present, the verifier anchors
  // to these witness keys — fetched by the caller from the witness's published
  // well-known endpoint — with trustAnchorMode "external", making it a genuine
  // independent third-party check rather than the recorded path's integrity
  // self-check. Absent -> the recorded "self" path (embedded witness keys).
  externalTrust?: { trustedWitnessKeys: WitnessKey[] };
}

export async function runDemoVerification(
  loaded: LoadedReceipt,
  options: RunDemoOptions = {}
): Promise<DemoVerificationResult> {
  // 1-2. Install the receipt's files into the shared virtual FS so the
  // verifier (which reads through node:fs/promises) can find them.
  installReceiptInVirtualFs(loaded);

  // Register the tampered schema AFTER install (which clears the FS), so the
  // verifier's schema read resolves to it instead of the fetched registry copy.
  if (options.schemaOverride) {
    registerFile(options.schemaOverride.path, options.schemaOverride.contents);
  }

  // 3. Parse the envelope and derive the trust anchor from the receipt
  // itself. "self" mode trusts the witness key the receipt carries.
  const envelopePath = path.join(loaded.packageDirectory, "receipt.json");
  const envelopeText = getFile(envelopePath);
  if (envelopeText === undefined) {
    throw new Error(`No receipt.json found in ${loaded.packageDirectory}`);
  }
  const receipt = JSON.parse(envelopeText) as AgentActionReceipt;

  // 4. Run the real verifier. Live mode anchors to the witness's published
  // well-known keys (external, third-party check); the recorded path anchors to
  // the witness key embedded in the receipt (self, integrity self-check).
  const report = options.externalTrust
    ? await verifyReceiptPackage(loaded.packageDirectory, {
        trustedWitnessKeys: options.externalTrust.trustedWitnessKeys,
        trustAnchorMode: "external"
      })
    : await verifyReceiptPackage(loaded.packageDirectory, {
        trustedWitnessKeys: witnessKeysFromReceipt(receipt),
        trustAnchorMode: "self"
      });

  return { report, receipt };
}
